import invariant from 'tiny-invariant';
import * as z from 'zod';

const stage = inferStage();

const Config = z.strictObject({
  phpApiBase: z.string(),
  dotNetApiBase: z.string(),
  blurbyBase: z.string().url(),
  blurbyProjectsBase: z.string().url(),
  blurbyDashboard: z.string().url(),
  blurbyAccount: z.string().url(),
  blurbyLogout: z.string().url(),
  supportBase: z.string().url(),
  analyticsBase: z.string().url(),
  shareBase: z.string().url(),
  forgotPasswordBase: z.string().url(),
  rumApplicationId: z.string().optional(),
  rumApplicationVersion: z.string().optional(),
  rumIdentityPoolId: z.string().optional(),
  rumEndpoint: z.string().url().optional(),
  awsRegion: z.string().optional(),
});

export const {
  phpApiBase,
  dotNetApiBase,
  blurbyBase,
  blurbyProjectsBase,
  blurbyDashboard,
  blurbyAccount,
  blurbyLogout,
  supportBase,
  analyticsBase,
  shareBase,
  forgotPasswordBase,
  rumApplicationId,
  rumApplicationVersion,
  rumIdentityPoolId,
  rumEndpoint,
  awsRegion,
} = initConfig();

function initConfig() {
  if (process.env.NODE_ENV === 'test') {
    return Config.parse({
      phpApiBase: 'https://php-api-base.test',
      dotNetApiBase: 'https://dot-net-api-base.test',
      blurbyBase: 'https://blurby-base.test',
      blurbyProjectsBase: 'https://blurby-projects-base.test',
      blurbyDashboard: 'https://blurby-dashboard.test',
      blurbyAccount: 'https://blurby-account.test',
      blurbyLogout: 'https://blurby-logout.test',
      supportBase: 'https://support-base.test',
      analyticsBase: 'https://analytics-base.test',
      shareBase: 'https://share-base.test',
      forgotPasswordBase: 'https://forgotPassword.test',
    });
  }
  const elem = document.querySelector('#config');
  invariant(elem.type === 'application/json');

  // TODO Add config to the DOM at request or build time
  if (!elem.innerHTML.length) {
    // Until then, inject the config at runtime
    injectConfig(elem);
  }

  return Config.parse(JSON.parse(elem.innerHTML));
}

function injectConfig(elem) {
  const { origin } = location;

  const phpApiBase = '/php-api/';
  const dotNetApiBase = '/dot-net-api/';

  const blurbyHostname = (() => {
    if (isGammaOrProd()) {
      return 'www.blurb.com';
    }
    return 'master.blurb.com';
  })();

  const blurbyBase = `https://${blurbyHostname}`;
  const blurbyProjectsBase = `${blurbyBase}/projects/`;
  const blurbyDashboard = `${blurbyBase}/my/dashboard/`;
  const blurbyAccount = `${blurbyBase}/my/account/`;
  const blurbyLogout = `${blurbyBase}/my/account/logout/`;
  const supportBase = `https://${blurbyHostname}/support`;
  const forgotPasswordBase = `https://${blurbyHostname}/my/account/forgot_password`;

  const analyticsBase = (() => {
    if (isProd()) {
      return 'https://clientevents.blurb.com/notice';
    }
    return 'https://pdx-clientevents-master.vip.blurb.com/notice';
  })();

  const shareBase = (() => {
    return `${origin}/share/`;
  })();

  let config = {
    phpApiBase,
    dotNetApiBase,
    blurbyBase,
    blurbyProjectsBase,
    blurbyDashboard,
    blurbyAccount,
    blurbyLogout,
    supportBase,
    analyticsBase,
    shareBase,
    forgotPasswordBase,
  };

  setRumConfig(config);
  elem.innerHTML = JSON.stringify(Config.parse(config), null, 2);
}

function setRumConfig(config) {
  if (isProd()) {
    config.rumApplicationId = '92cdfe11-2ca1-4120-87bd-643941cb3851';
    config.rumApplicationVersion = '1.0.0';
    config.rumIdentityPoolId = 'us-west-1:67b403e0-4452-496d-a094-bca9cb41265a';
    config.rumEndpoint = 'https://dataplane.rum.us-west-1.amazonaws.com';
    config.awsRegion = 'us-west-1';
  } else if (isGamma()) {
    config.rumApplicationId = '429d3945-2990-416f-92c1-a997e94b53a5';
    config.rumApplicationVersion = '1.0.0';
    config.rumIdentityPoolId = 'us-west-1:2eede5df-09c9-4f13-a579-2f192304d47f';
    config.rumEndpoint = 'https://dataplane.rum.us-west-1.amazonaws.com';
    config.awsRegion = 'us-west-1';
  }
}

export function inferStage() {
  if (localStorage.getItem('dev.stage')) {
    return z
      .enum(['alpha', 'gamma', 'prod'])
      .parse(localStorage.getItem('dev.stage'));
  }
  switch (location.hostname) {
    case 'build.blurb.com':
      return 'prod';
    case 'gamma.build.blurb.com':
      return 'gamma';
    default:
      return 'alpha';
  }
}

export function isProd() {
  return stage === 'prod';
}

export function isGamma() {
  return stage === 'gamma';
}

export function isGammaOrProd() {
  return isProd() || isGamma();
}
